import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import { AuthForm } from '@/features/auth/AuthForm';
import { SetPasswordForm } from '@/features/auth/SetPasswordForm';
import {
  usePasswordResetLinkQuery,
  useRequestPasswordResetMutation,
  useResetPasswordMutation,
  useVerificationQuery,
  useVerifyEmailMutation,
} from '@/shared/api/endpoints';
import { errorCode } from '@/shared/api/errors';
import { Button } from '@/shared/ui/Button';
import { Alert, Card, Spinner, TextField } from '@/shared/ui/primitives';

export function LoginPage() {
  return <AuthForm mode="login" />;
}

export function RegisterPage() {
  return <AuthForm mode="register" />;
}

/**
 * Where the link in the mail leads. For a new account it is the last step of
 * registering: the password is chosen here. For an account that has a password
 * (the ones the installation starts with) it confirms the address and says so.
 */
export function VerifyEmailPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token');
  // Asked once: the answer stops being true the moment the link is used.
  const link = useVerificationQuery(token ?? '', {
    skip: !token,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });
  const [verify, state] = useVerifyEmailMutation();
  const confirmOnly = link.data?.passwordRequired === false;
  // A link works once: it must not be sent twice, even when effects run twice in development.
  const sent = useRef(false);

  useEffect(() => {
    if (!token || !confirmOnly || sent.current) return;
    sent.current = true;
    void verify({ token });
  }, [token, confirmOnly, verify]);

  const failed = !token || link.isError || state.isError;
  const choosing =
    !failed && token && link.data?.passwordRequired
      ? { token, email: link.data.email, handle: link.data.handle ?? '' }
      : null;
  return (
    <Card className="mx-auto max-w-md space-y-4">
      <h1 className="text-2xl font-bold">
        {t(choosing ? 'auth.verify.password.title' : 'auth.verify.title')}
      </h1>
      {failed ? (
        <Alert tone="bad" live>
          {t('auth.verify.failed')}
        </Alert>
      ) : choosing ? (
        <SetPasswordForm token={choosing.token} email={choosing.email} handle={choosing.handle} />
      ) : state.isSuccess ? (
        <Alert tone="ok" live>
          {t('auth.verify.done')}
        </Alert>
      ) : (
        <Spinner label={t('loading')} />
      )}
      {choosing ? null : (
        <p className="flex justify-center gap-6">
          <Link to="/login" className="font-medium text-brand hover:underline">
            {t('auth.login.submit')}
          </Link>
          {failed ? (
            <Link to="/register" className="font-medium text-brand hover:underline">
              {t('auth.verify.registerAgain')}
            </Link>
          ) : null}
        </p>
      )}
    </Card>
  );
}

/**
 * The password is forgotten: a link to choose another is mailed. The page says the
 * same whether or not the address has an account, as the server does.
 */
export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [ask, state] = useRequestPasswordResetMutation();
  const submit = (event: FormEvent) => {
    event.preventDefault();
    void ask(email);
  };
  return (
    <Card className="mx-auto max-w-md space-y-4">
      <h1 className="text-2xl font-bold">{t('auth.forgot.title')}</h1>
      {state.isSuccess ? (
        <Alert tone="ok" live>
          {t('auth.forgot.sent', { email })}
        </Alert>
      ) : (
        <form className="space-y-4" onSubmit={submit}>
          <p className="text-sm text-muted">{t('auth.forgot.help')}</p>
          <TextField
            label={t('auth.email')}
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            required
          />
          {state.isError ? (
            <Alert tone="bad" live>
              {t('errors.unknown')}
            </Alert>
          ) : null}
          <Button type="submit" size="lg" className="w-full" disabled={state.isLoading}>
            {t('auth.forgot.submit')}
          </Button>
        </form>
      )}
      <p className="text-center text-sm">
        <Link to="/login" className="font-medium text-brand hover:underline">
          {t('auth.login.submit')}
        </Link>
      </p>
    </Card>
  );
}

/** Where the link of a new-password mail leads: the new password is chosen here. */
export function ResetPasswordPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const link = usePasswordResetLinkQuery(token, {
    skip: !token,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });
  const [password, setPassword] = useState('');
  const [reset, state] = useResetPasswordMutation();
  const failure = state.error ? (errorCode(state.error) ?? 'unknown') : null;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    void reset({ token, password });
  };
  const broken = !token || link.isError || failure === 'invalid_verification_token';
  return (
    <Card className="mx-auto max-w-md space-y-4">
      <h1 className="text-2xl font-bold">{t('auth.reset.title')}</h1>
      {broken ? (
        <Alert tone="bad" live>
          {t('auth.reset.failed')}
        </Alert>
      ) : state.isSuccess ? (
        <Alert tone="ok" live>
          {t('auth.reset.done')}
        </Alert>
      ) : link.data ? (
        <form className="space-y-4" onSubmit={submit}>
          <p className="text-sm text-muted">{t('auth.reset.for', { email: link.data.email })}</p>
          <TextField
            label={t('auth.reset.password')}
            hint={t('auth.reset.hint')}
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="new-password"
            minLength={8}
            maxLength={128}
            required
          />
          {failure ? (
            <Alert tone="bad" live>
              {t(`errors.${failure}`, { defaultValue: t('errors.unknown'), minimum: 12 })}
            </Alert>
          ) : null}
          <Button type="submit" size="lg" className="w-full" disabled={state.isLoading}>
            {t('auth.reset.submit')}
          </Button>
        </form>
      ) : (
        <Spinner label={t('loading')} />
      )}
      {state.isSuccess || broken ? (
        <p className="flex justify-center gap-6">
          <Link to="/login" className="font-medium text-brand hover:underline">
            {t('auth.login.submit')}
          </Link>
          {broken ? (
            <Link to="/forgot-password" className="font-medium text-brand hover:underline">
              {t('auth.reset.again')}
            </Link>
          ) : null}
        </p>
      ) : null}
    </Card>
  );
}

export function NotFoundPage() {
  const { t } = useTranslation();
  return (
    <Card className="mx-auto max-w-md text-center">
      <h1 className="text-2xl font-bold">{t('notFound.title')}</h1>
      <p className="mt-2 text-muted">{t('notFound.text')}</p>
      <Link to="/" className="mt-4 inline-block font-medium text-brand hover:underline">
        {t('nav.hub')}
      </Link>
    </Card>
  );
}
